/**
 * Wunsch-Los: customers build a lot from the live singles pool.
 */
(() => {
  const root = document.querySelector('[data-wunsch-los]');
  if (!root || !window.Kl24SinglesPool) return;

  const poolApi = window.Kl24SinglesPool;
  const CART_SECTIONS = ['cart-icon-bubble', 'cart-drawer'];
  const collectionHandle = root.dataset.collectionHandle || 'pokemon-tcg';
  const attrNum = (name) => {
    const value = Number(root.getAttribute(name));
    return Number.isFinite(value) ? value : 0;
  };
  const minCards = Math.max(1, attrNum('data-min-cards') || 25);
  const minCents = Math.max(0, attrNum('data-min-cents') || 500);

  const statusEl = root.querySelector('[data-lot-status]');
  const poolEl = root.querySelector('[data-lot-pool]');
  const summaryEl = root.querySelector('[data-lot-summary]');
  const priceEl = root.querySelector('[data-lot-price]');
  const listEl = root.querySelector('[data-lot-list]');
  const feedbackEl = root.querySelector('[data-lot-feedback]');
  const addBtn = root.querySelector('[data-lot-add]');
  const rollBtn = root.querySelector('[data-lot-roll]');
  const setsEl = root.querySelector('[data-lot-sets]');
  const wishSearch = root.querySelector('[data-lot-wish]');
  const wishResults = root.querySelector('[data-lot-wish-results]');
  const wishPicked = root.querySelector('[data-lot-wish-picked]');
  const countInput = root.querySelector('[data-lot-count]');
  const budgetInput = root.querySelector('[data-lot-budget]');

  let catalog = [];
  let sets = [];
  let ready = false;
  let adding = false;
  let reroll = false;
  let rolling = false;
  /** Queue roll/add while a draw is in flight (do not discard taps). */
  let pendingRoll = false;
  let pendingAdd = false;
  let edited = false;
  let reached = false;
  let reason = '';
  let wishInCart = false;
  let language = 'de';
  let holoMode = 'some';
  let cardKindMode = 'pokemon';
  let sizeMode = 'count';
  let countPreset = 50;
  let lastCount = 50;
  let lastCustomCount = 0;
  let lastBudget = '';
  let wish = null;
  let drawn = [];
  let cartVariantIds = new Set();
  let latestCart = null;
  let timer = 0;
  let idleHandle = 0;
  let drawGen = 0;
  let matchingCount = 0;
  const MAX_ENERGY_TYPES = 4;
  const TRAINER_SHARE_PERCENT = 20;
  let mixOrder = [];
  let mixPercents = new Map();
  let mixHints = [];

  const formatMoney = (cents) => {
    const fmt = window.formatMoneyDE || window.Kl24EuroFormat?.formatMoneyDE;
    if (fmt) return fmt(cents);
    const value = Math.max(0, Number(cents) || 0) / 100;
    const amount = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return `${amount}\u00A0€`;
  };

  const THUMB_WIDTH = 200;
  const EAGER_THUMBS = 8;

  const thumbUrl = (url) => {
    const api = window.Kl24ShopifyImage;
    if (api?.sizedUrl) return api.sizedUrl(url, THUMB_WIDTH);
    const raw = String(url || '').trim();
    if (!raw || !raw.toLowerCase().includes('cdn.shopify.com')) return raw;
    try {
      const parsed = new URL(raw, window.location.origin);
      parsed.searchParams.set('width', String(THUMB_WIDTH));
      return parsed.toString();
    } catch {
      return raw;
    }
  };

  const selectedEnergies = () =>
    mixOrder.length
      ? mixOrder.slice()
      : [...root.querySelectorAll('[data-lot-energy][aria-pressed="true"]')].map((button) => button.dataset.lotEnergy);

  const energyPaint = (label) => {
    const key = String(label || '').trim().toLowerCase();
    const labels = poolApi.ENERGY_LABELS || {};
    const slug = labels[key]
      ? key
      : Object.entries(labels).find(([, name]) => String(name).toLowerCase() === key)?.[0] || '';
    if (!slug) return { color: 'rgb(var(--kl24-accent))', ink: 'rgb(var(--kl24-accent-ink))' };
    return {
      color: `rgb(var(--kl24-energy-${slug}))`,
      ink: `rgb(var(--kl24-energy-${slug}-ink))`,
    };
  };

  const syncLotEnergy = () => {
    const activeLabel = selectedEnergies()[0];
    const paint = activeLabel ? energyPaint(activeLabel) : null;
    root.style.setProperty('--lot-energy', paint ? paint.color : 'rgb(var(--kl24-accent))');
  };

  const equalPercents = (count) => {
    if (count <= 1) return [100];
    if (count === 2) return [50, 50];
    if (count === 3) return [33, 33, 34];
    return [25, 25, 25, 25];
  };

  const snapPercent = (value) => Math.round(Number(value) / 5) * 5;

  const normalizeMixPercents = (map, labels = [...map.keys()]) => {
    if (!labels.length) return new Map();
    const min = labels.length > 1 ? 5 : 0;
    const values = labels.map((label) => Math.max(0, Math.round(Number(map.get(label)) || 0)));
    let sum = values.reduce((total, value) => total + value, 0);
    if (sum <= 0) {
      const equal = equalPercents(labels.length);
      return new Map(labels.map((label, index) => [label, equal[index] || 0]));
    }
    if (sum !== 100) {
      values[values.length - 1] += 100 - sum;
    }
    for (let i = 0; i < values.length; i += 1) {
      if (values[i] < min) {
        const need = min - values[i];
        values[i] = min;
        for (let j = 0; j < values.length && need > 0; j += 1) {
          if (j === i || values[j] <= min) continue;
          const take = Math.min(values[j] - min, need);
          values[j] -= take;
        }
      }
    }
    let fixed = values.reduce((total, value) => total + value, 0);
    if (fixed !== 100 && values.length) values[values.length - 1] += 100 - fixed;
    return new Map(labels.map((label, index) => [label, values[index] || 0]));
  };

  const mixSumOk = (shares = mixShares()) => {
    if (shares.length < 2) return true;
    const total = shares.reduce((sum, share) => sum + Number(share.percent || 0), 0);
    return Math.abs(total - 100) < 0.51;
  };

  const redistributePercents = (labels, lockedLabel, lockedPercent) => {
    const n = labels.length;
    if (n <= 1) return new Map(labels.map((label) => [label, 100]));
    const min = 5;
    const max = 100 - min * (n - 1);
    const locked = Math.min(max, Math.max(min, snapPercent(lockedPercent)));
    const others = labels.filter((label) => label !== lockedLabel);
    const weights = others.map((label) => Math.max(mixPercents.get(label) || 0, min));
    const weightSum = weights.reduce((sum, value) => sum + value, 0) || others.length;
    let remaining = 100 - locked;
    const raw = others.map((label, index) => ({
      label,
      value: (remaining * weights[index]) / weightSum,
    }));
    const floors = raw.map((entry) => ({
      label: entry.label,
      value: Math.max(min, Math.floor(entry.value / 5) * 5),
    }));
    let used = floors.reduce((sum, entry) => sum + entry.value, 0);
    let leftover = remaining - used;
    const order = raw
      .map((entry, index) => ({ index, frac: entry.value - floors[index].value }))
      .sort((a, b) => b.frac - a.frac || a.index - b.index);
    let guard = 0;
    while (leftover >= 5 && guard < 40) {
      const target = floors[order[guard % order.length].index];
      target.value += 5;
      leftover -= 5;
      used += 5;
      guard += 1;
    }
    if (leftover !== 0 && floors.length) {
      floors[0].value += leftover;
    }
    const next = new Map([[lockedLabel, locked]]);
    floors.forEach((entry) => next.set(entry.label, entry.value));
    return normalizeMixPercents(next, labels);
  };

  const applyEqualMix = (labels) => {
    const values = equalPercents(labels.length);
    mixPercents = normalizeMixPercents(
      new Map(labels.map((label, index) => [label, values[index] || 0])),
      labels
    );
  };

  const mixShares = () => {
    const labels = selectedEnergies();
    if (labels.length < 2) return [];
    if (labels.some((label) => !mixPercents.has(label)) || !mixSumOk(
      labels.map((label) => ({ percent: Number(mixPercents.get(label)) || 0 }))
    )) {
      mixPercents = normalizeMixPercents(mixPercents, labels);
    }
    return labels.map((label) => ({
      label,
      percent: Number(mixPercents.get(label)) || 0,
      paint: energyPaint(label),
    }));
  };

  const allocateParts = (total, percents) => {
    if (!percents.length) return [];
    if (total <= 0) return percents.map(() => 0);
    const raw = percents.map((percent) => (total * percent) / 100);
    const parts = raw.map((value) => Math.floor(value));
    let left = total - parts.reduce((sum, value) => sum + value, 0);
    const order = raw
      .map((value, index) => ({ index, frac: value - parts[index] }))
      .sort((a, b) => b.frac - a.frac || a.index - b.index);
    for (let i = 0; i < left; i += 1) parts[order[i % order.length].index] += 1;
    return parts;
  };

  const mixQuotaLabel = (share, countPart, budgetPart) => {
    if (sizeMode === 'budget') return `${share.label} ${share.percent} % · ${formatMoney(budgetPart)}`;
    return `${share.label} ${share.percent} % · ${countPart} ${countPart === 1 ? 'Karte' : 'Karten'}`;
  };

  const mixTargets = (shares) => {
    const validation = validate();
    return {
      countParts: allocateParts(
        validation.count || 0,
        shares.map((share) => share.percent)
      ),
      budgetParts: allocateParts(
        validation.budgetCents || 0,
        shares.map((share) => share.percent)
      ),
    };
  };

  const syncMixReadouts = () => {
    const list = root.querySelector('[data-lot-mix-list]');
    const bar = root.querySelector('[data-lot-mix-bar]');
    if (!list || !bar) return;
    const shares = mixShares();
    const { countParts, budgetParts } = mixTargets(shares);
    [...list.children].forEach((row, index) => {
      const share = shares[index];
      if (!share) return;
      const range = row.querySelector('.kl24-builder__mix-range');
      const meta = row.querySelector('.kl24-builder__mix-meta');
      if (range && document.activeElement !== range) range.value = String(share.percent);
      if (range && document.activeElement === range) range.value = String(share.percent);
      if (meta) meta.textContent = mixQuotaLabel(share, countParts[index] || 0, budgetParts[index] || 0);
    });
    [...bar.children].forEach((seg, index) => {
      const share = shares[index];
      if (!share) return;
      seg.style.flex = String(Math.max(share.percent, 1));
    });
  };

  const renderMix = () => {
    const host = root.querySelector('[data-lot-mix]');
    const list = root.querySelector('[data-lot-mix-list]');
    const bar = root.querySelector('[data-lot-mix-bar]');
    if (!host || !list || !bar) return;
    const shares = mixShares();
    host.hidden = shares.length < 2;
    if (shares.length < 2) {
      list.replaceChildren();
      bar.replaceChildren();
      return;
    }
    const activeLabel = document.activeElement?.classList?.contains('kl24-builder__mix-range')
      ? document.activeElement.getAttribute('aria-label')
      : '';
    const labelsKey = shares.map((share) => share.label).join('|');
    if (list.dataset.mixKey === labelsKey && list.children.length === shares.length) {
      syncMixReadouts();
      return;
    }
    list.dataset.mixKey = labelsKey;
    const { countParts, budgetParts } = mixTargets(shares);
    list.replaceChildren(
      ...shares.map((share, index) => {
        const row = document.createElement('li');
        row.className = 'kl24-builder__mix-row';
        row.style.setProperty('--energy', share.paint.color);
        const dot = document.createElement('span');
        dot.className = 'kl24-builder__mix-dot';
        dot.setAttribute('aria-hidden', 'true');
        const name = document.createElement('span');
        name.className = 'kl24-builder__mix-name';
        name.textContent = share.label;
        const range = document.createElement('input');
        range.type = 'range';
        range.className = 'kl24-builder__mix-range';
        range.min = '5';
        range.max = String(100 - 5 * (shares.length - 1));
        range.step = '1';
        range.value = String(share.percent);
        range.setAttribute('aria-label', `${share.label} Anteil`);
        range.addEventListener('input', () => {
          mixPercents = redistributePercents(
            shares.map((entry) => entry.label),
            share.label,
            Number(range.value)
          );
          syncMixReadouts();
          scheduleDraw();
        });
        const meta = document.createElement('span');
        meta.className = 'kl24-builder__mix-meta';
        meta.textContent = mixQuotaLabel(share, countParts[index] || 0, budgetParts[index] || 0);
        row.append(dot, name, range, meta);
        return row;
      })
    );
    bar.replaceChildren(
      ...shares.map((share) => {
        const seg = document.createElement('span');
        seg.className = 'kl24-builder__mix-bar-seg';
        seg.style.setProperty('--energy', share.paint.color);
        seg.style.flex = String(Math.max(share.percent, 1));
        return seg;
      })
    );
    if (activeLabel) {
      const again = list.querySelector(`[aria-label="${CSS.escape(activeLabel)}"]`);
      again?.focus({ preventScroll: true });
    }
  };

  const selectedSets = () => {
    const codes = new Set();
    setsEl?.querySelectorAll('input:checked').forEach((input) => {
      if (input.value) codes.add(input.value);
    });
    return codes;
  };

  const currentCount = () => {
    if (countPreset === 'custom') {
      const value = Number.parseInt(String(countInput?.value || ''), 10);
      if (!Number.isFinite(value) || value <= 0) return 0;
      const capped = Math.min(poolApi.MAX_TOTAL, value);
      lastCustomCount = capped;
      return capped;
    }
    return Number(countPreset) || 0;
  };

  const currentBudget = () => {
    // Re-query: section markup can hydrate after the initial const capture.
    const input = root.querySelector('[data-lot-budget]') || budgetInput;
    const euros = Number(String(input?.value || '').replace(',', '.'));
    if (!Number.isFinite(euros) || euros <= 0) return 0;
    return Math.round(euros * 100);
  };

  const cardKindsForMode = () => {
    if (cardKindMode === 'trainer') return ['trainer'];
    if (cardKindMode === 'mixed') return ['pokemon', 'trainer'];
    return ['pokemon'];
  };

  const syncCartBlocklist = async () => {
    try {
      const res = await fetch(`/cart.js?_=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!res.ok) return cartVariantIds;
      const cart = await res.json();
      latestCart = cart;
      cartVariantIds = new Set(
        (cart.items || []).map((item) => String(item.variant_id || item.id || '')).filter(Boolean)
      );
    } catch (error) {
      console.warn('[Wunsch-Los] cart.js', error);
    }
    return cartVariantIds;
  };

  const applyCartIconBubble = (html) => {
    const target = document.getElementById('cart-icon-bubble');
    if (!target || !html) return;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.shopify-section') || doc.body;
      if (section) target.innerHTML = section.innerHTML;
    } catch (error) {
      console.warn('[Wunsch-Los] cart-icon-bubble', error);
    }
  };

  const refreshCartChrome = async (parsed) => {
    refreshDrawer(parsed);
    const bubbleHtml = parsed?.sections?.['cart-icon-bubble'];
    if (bubbleHtml) {
      applyCartIconBubble(bubbleHtml);
    } else {
      try {
        const res = await fetch(`/cart?section_id=cart-icon-bubble&_=${Date.now()}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (res.ok) applyCartIconBubble(await res.text());
      } catch (error) {
        console.warn('[Wunsch-Los] cart bubble fetch', error);
      }
    }
    if (window.Kl24InventoryReservation?.refreshCartChrome) {
      await window.Kl24InventoryReservation.refreshCartChrome(parsed, {
        openDrawer: false,
        source: 'wunsch-los',
      });
    }
  };

  const baseFilter = (extra = {}) => {
    const blocked = new Set(cartVariantIds);
    if (extra.blockedIds) {
      const more = extra.blockedIds instanceof Set ? extra.blockedIds : new Set(extra.blockedIds || []);
      more.forEach((id) => {
        if (id != null && id !== '') blocked.add(String(id));
      });
    }
    return poolApi.filter(catalog, {
      nmOnly: true,
      language,
      setCodes: selectedSets(),
      holoMode: holoMode === 'none' ? 'none' : '',
      cardKinds: cardKindsForMode(),
      ...extra,
      blockedIds: blocked,
    });
  };

  const filtered = () => {
    if (cardKindMode === 'trainer') {
      return baseFilter({
        energyTypes: [],
        cardKinds: ['trainer'],
      });
    }
    const energies = selectedEnergies();
    return baseFilter({
      energyTypes: energies,
      cardKinds: cardKindMode === 'mixed' ? ['pokemon', 'trainer'] : ['pokemon'],
    });
  };

  const filteredPokemon = (extra = {}) =>
    baseFilter({
      cardKinds: ['pokemon'],
      ...extra,
    });

  const filteredEnergy = (label, blockedIds) =>
    filteredPokemon({
      energyTypes: label ? [label] : [],
      blockedIds,
    });

  const filteredTrainers = (blockedIds) =>
    baseFilter({
      cardKinds: ['trainer'],
      energyTypes: [],
      blockedIds,
    });

  const syncCardKindUi = () => {
    const host = root.querySelector('[data-lot-energies]');
    const mixHost = root.querySelector('[data-lot-mix]');
    const note = root.querySelector('[data-lot-energy-note]');
    const kindHint = root.querySelector('[data-lot-card-kind-hint]');
    const trainerOnly = cardKindMode === 'trainer';
    host?.classList.toggle('is-disabled', trainerOnly);
    host?.setAttribute('aria-disabled', trainerOnly ? 'true' : 'false');
    host?.querySelectorAll('[data-lot-energy]').forEach((button) => {
      const label = button.dataset.lotEnergy || '';
      const inStock = energyStockCount(label) >= MIN_ENERGY_STOCK;
      button.disabled = trainerOnly || !inStock;
      if (trainerOnly) {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-active');
      }
    });
    mixHost?.classList.toggle('is-disabled', trainerOnly);
    mixHost?.setAttribute('aria-disabled', trainerOnly ? 'true' : 'false');
    mixHost?.querySelectorAll('input, button').forEach((el) => {
      el.disabled = trainerOnly;
    });
    if (note) {
      note.hidden = !trainerOnly;
    }
    if (kindHint) kindHint.hidden = cardKindMode !== 'mixed';
    if (trainerOnly && mixOrder.length) {
      mixOrder = [];
      mixPercents = new Map();
      syncLotEnergy();
      renderMix();
      mixHost?.classList.add('is-disabled');
    }
  };

  const showFeedback = (message, type) => {
    if (!feedbackEl) return;
    feedbackEl.hidden = !message;
    feedbackEl.textContent = message || '';
    feedbackEl.classList.toggle('is-error', type === 'error');
    feedbackEl.classList.toggle('is-success', type === 'success');
    feedbackEl.classList.toggle('is-info', type === 'info');
  };

  const meetsMinimum = (cards, sum) => cards.length >= minCards || sum >= minCents;

  const loosenHint = 'Filter lockern (z.B. Sprache egal, mehr Typen, Holo-Anteil)?';

  const matchingMessage = (count) => {
    const noun = count === 1 ? 'passende Karte' : 'passende Karten';
    return `Mit diesen Filtern gibt es nur ${count} ${noun} – ${loosenHint}`;
  };

  const rememberSize = () => {
    if (sizeMode === 'count') {
      const count = currentCount();
      if (count) lastCount = count;
      return;
    }
    if (currentBudget() && budgetInput) lastBudget = budgetInput.value.trim();
  };

  const lotMinimumHint = () =>
    `Mindestbudget ${formatMoney(minCents)} · oder mindestens ${minCards} Karten`;

  const syncBudgetHint = () => {
    const wrap = root.querySelector('[data-lot-budget-wrap]');
    if (!wrap) return;
    let hint = wrap.querySelector('[data-lot-budget-min]');
    if (!hint) {
      hint = document.createElement('p');
      hint.className = 'kl24-builder__hint';
      hint.dataset.lotBudgetMin = '1';
      wrap.append(hint);
    }
    hint.textContent = lotMinimumHint();
  };

  const validate = () => {
    if (sizeMode === 'count') {
      const count = currentCount();
      if (!count) return { ok: false, message: 'Bitte eine Kartenanzahl angeben.' };
      lastCount = count;
      return { ok: true, message: '', count, budgetCents: 0 };
    }
    const budgetCents = currentBudget();
    if (!budgetCents) return { ok: false, message: 'Bitte ein Budget angeben.' };
    if (budgetCents < minCents) {
      return {
        ok: false,
        message: `${lotMinimumHint()}. Bitte mindestens ${formatMoney(minCents)} eintragen.`,
        count: 0,
        budgetCents,
      };
    }
    if (budgetInput) lastBudget = budgetInput.value.trim();
    return { ok: true, message: '', count: 0, budgetCents };
  };

  const restoreCustomCount = () => {
    if (!countInput) return;
    countInput.hidden = false;
    // Never prefill with the active preset (e.g. 50) — typing would become "5025".
    if (lastCustomCount > 0) {
      countInput.value = String(lastCustomCount);
      countInput.focus({ preventScroll: true });
      countInput.select();
    } else {
      countInput.value = '';
      countInput.focus({ preventScroll: true });
    }
  };

  const syncBuilderBarClearance = () => {
    const bar = document.querySelector('.kl24-builder__bar');
    if (!bar) return;
    const height = Math.ceil(bar.getBoundingClientRect().height || 0);
    if (height < 40) return;
    // Bar height + small gap — tiles must sit above this via compact chrome, not z-index.
    document.documentElement.style.setProperty(
      '--kl24-builder-bar-space',
      `${height + 20}px`
    );
  };

  /** Replace cards held by other sessions so count/budget stay reachable. */
  const ensureReservableLot = async (cards, { targetCount = 0, force = false } = {}) => {
    const api = window.Kl24InventoryReservation;
    if (!api?.filterAvailableCards || !cards?.length) {
      return { cards: cards || [], replaced: 0 };
    }
    let working = cards.slice();
    let replaced = 0;
    const heldIds = new Set();

    for (let round = 0; round < 4; round += 1) {
      const { keep, blocked } = await api.filterAvailableCards(working, {
        force: force || round === 0,
      });
      blocked.forEach((card) => heldIds.add(String(card.variantId)));
      if (!blocked.length) {
        working = keep;
        break;
      }
      let next = keep.slice();
      const used = new Set([...next.map((card) => String(card.variantId)), ...heldIds]);
      for (const card of blocked) {
        const pool = openPoolForReplace(used, heldIds);
        const rep = poolApi.replacementFor(card, pool, used);
        replaced += 1;
        if (rep && !heldIds.has(String(rep.variantId))) {
          next.push(rep);
          used.add(String(rep.variantId));
        }
      }
      working = next;
    }

    // Final filter — never send unchecked / still-held cards.
    {
      const again = await api.filterAvailableCards(working, { force: true });
      again.blocked.forEach((card) => heldIds.add(String(card.variantId)));
      working = again.keep;
    }

    if (targetCount > 0 && working.length < targetCount) {
      for (let fillRound = 0; fillRound < 5 && working.length < targetCount; fillRound += 1) {
        const used = new Set([...working.map((card) => String(card.variantId)), ...heldIds]);
        const pool = openPoolForReplace(used, heldIds);
        let added = 0;
        while (working.length < targetCount && pool.length) {
          const cand = pool.find((card) => !used.has(String(card.variantId)));
          if (!cand) break;
          used.add(String(cand.variantId));
          working.push(cand);
          added += 1;
        }
        if (!added) break;
        const check = await api.filterAvailableCards(working, { force: true });
        check.blocked.forEach((card) => heldIds.add(String(card.variantId)));
        working = check.keep;
      }
    }
    return { cards: working, replaced, heldIds };
  };

  const restoreBudget = () => {
    if (!budgetInput) return;
    if (!currentBudget() && lastBudget) budgetInput.value = lastBudget;
    budgetInput.focus();
  };

  const renderPoolCount = () => {
    if (!poolEl) return;
    const count = ready ? filtered().length : 0;
    poolEl.textContent = ready ? `${count} Karten passen aktuell` : 'Karten werden geladen …';
  };

  const CONDITION_LABELS = poolApi.CONDITION_LABELS || {
    NM: 'Near Mint',
    EX: 'Excellent',
    LP: 'Lightly Played',
    MP: 'Moderately Played',
    DMG: 'Damaged',
  };

  const conditionCode = (card) => String(card?.condition || '').toUpperCase();

  const conditionPill = (card) => {
    const code = conditionCode(card);
    const label = CONDITION_LABELS[code];
    if (!label) return null;
    const pill = document.createElement('span');
    pill.className = `wunsch-los__condition wunsch-los__condition--${code.toLowerCase()}`;
    pill.title = label;
    pill.setAttribute('aria-label', `Zustand: ${label} (${code})`);
    const mark = document.createElement('span');
    mark.className = 'wunsch-los__condition-code';
    mark.textContent = code;
    mark.setAttribute('aria-hidden', 'true');
    const text = document.createElement('span');
    text.className = 'wunsch-los__condition-label';
    text.textContent = label;
    pill.append(mark, text);
    return pill;
  };

  const formatCardLine = (card) => {
    const parts = [
      card.cardName,
      [card.setName || card.setCode, card.cardNumber].filter(Boolean).join(' '),
      conditionCode(card) || null,
      formatMoney(card.priceCents),
    ].filter(Boolean);
    return parts.join(' · ');
  };

  const renderWish = () => {
    if (!wishPicked) return;
    wishPicked.replaceChildren();
    if (!wish) return;
    const item = document.createElement('li');
    const line = document.createElement('span');
    line.textContent = formatCardLine(wish);
    item.append(line);
    if (cartVariantIds.has(String(wish.variantId))) {
      const note = document.createElement('span');
      note.className = 'kl24-builder__wish-note';
      note.textContent = ' — liegt bereits im Warenkorb';
      item.append(note);
    }
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'kl24-builder__icon-btn';
    remove.setAttribute('aria-label', 'Wunsch-Pokémon entfernen');
    remove.textContent = '×';
    remove.addEventListener('click', () => {
      wish = null;
      wishInCart = false;
      scheduleDraw();
    });
    item.append(remove);
    wishPicked.append(item);
  };

  let zoomEl = null;
  let zoomAnchor = null;

  const previewUrl = (url) => {
    const api = window.Kl24ShopifyImage;
    if (api?.sizedUrl) return api.sizedUrl(url, api.WIDTHS?.cardPreview || 600);
    return url;
  };

  const hideZoom = () => {
    zoomAnchor = null;
    if (!zoomEl) return;
    zoomEl.hidden = true;
  };

  const placeZoom = (anchor) => {
    if (!zoomEl || zoomEl.hidden) return;
    const box = anchor.getBoundingClientRect();
    const width = Math.min(280, window.innerWidth - 24);
    const height = Math.round(width * (88 / 63)) + 8;
    let left = box.right + 12;
    if (left + width > window.innerWidth - 12) left = box.left - width - 12;
    if (left < 12) left = Math.max(12, (window.innerWidth - width) / 2);
    let top = box.top;
    if (top + height > window.innerHeight - 12) top = Math.max(12, window.innerHeight - height - 12);
    zoomEl.style.width = `${width}px`;
    zoomEl.style.left = `${left}px`;
    zoomEl.style.top = `${top}px`;
  };

  const showZoom = (anchor, card) => {
    if (!card?.imageUrl) return;
    if (!zoomEl) {
      zoomEl = document.createElement('div');
      zoomEl.className = 'wunsch-los__zoom';
      zoomEl.hidden = true;
      const img = document.createElement('img');
      img.alt = '';
      zoomEl.append(img);
      document.body.append(zoomEl);
      window.addEventListener('scroll', hideZoom, true);
      window.addEventListener('resize', hideZoom);
    }
    const img = zoomEl.querySelector('img');
    img.alt = card.cardName || '';
    if (img.getAttribute('src') !== previewUrl(card.imageUrl)) img.src = previewUrl(card.imageUrl);
    zoomEl.hidden = false;
    zoomAnchor = anchor;
    placeZoom(anchor);
  };

  const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const renderCard = (card, index) => {
    const item = document.createElement('li');
    item.className = 'kl24-builder__card';
    item.dataset.variantId = card.variantId;
    const paint = energyPaint(card.energyType);
    item.style.setProperty('--energy', paint.color);
    const photo = document.createElement(card.imageUrl ? 'button' : 'span');
    photo.className = 'kl24-builder__thumb';
    if (card.imageUrl) {
      photo.type = 'button';
      photo.setAttribute('aria-label', `${card.cardName || 'Karte'} vergrößern`);
      const img = document.createElement('img');
      img.alt = '';
      img.width = 56;
      img.height = 78;
      img.decoding = 'async';
      if (index < EAGER_THUMBS) {
        img.loading = 'eager';
        img.fetchPriority = 'high';
      } else img.loading = 'lazy';
      img.src = thumbUrl(card.imageUrl);
      photo.append(img);
      photo.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        showZoom(photo, card);
      });
      photo.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        hideZoom();
      });
      photo.addEventListener('focus', () => showZoom(photo, card));
      photo.addEventListener('blur', () => hideZoom());
      photo.addEventListener('click', () => {
        if (finePointer()) return;
        if (zoomAnchor === photo && zoomEl && !zoomEl.hidden) hideZoom();
        else showZoom(photo, card);
      });
    }
    const body = document.createElement('div');
    body.className = 'kl24-builder__copy';
    const nameRow = document.createElement('div');
    nameRow.className = 'wunsch-los__name-row';
    const name = document.createElement('p');
    name.className = 'kl24-builder__name';
    name.textContent = card.cardName || card.title;
    nameRow.append(name);
    const pill = conditionPill(card);
    if (pill) nameRow.append(pill);
    const meta = document.createElement('p');
    meta.className = 'kl24-builder__set';
    meta.textContent = [card.setName || card.setCode, card.cardNumber].filter(Boolean).join(' · ');
    body.append(nameRow, meta);
    const price = document.createElement('p');
    price.className = 'kl24-builder__price';
    price.textContent = formatMoney(card.priceCents);
    const actions = document.createElement('div');
    actions.className = 'kl24-builder__actions';
    const swap = document.createElement('button');
    swap.type = 'button';
    swap.className = 'kl24-builder__swap';
    swap.textContent = 'Tauschen';
    swap.addEventListener('click', () => swapCard(card.variantId));
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'kl24-builder__icon-btn';
    remove.setAttribute('aria-label', 'Entfernen');
    remove.textContent = '×';
    remove.addEventListener('click', () => removeCard(card.variantId));
    actions.append(swap, remove);
    item.append(photo, body, price, actions);
    return item;
  };

  const render = () => {
    if (!listEl) return;
    const validation = validate();
    const sum = drawn.reduce((total, card) => total + card.priceCents, 0);
    let feedback = '';
    let feedbackType = '';
    let summary = '';

    if (!validation.ok) {
      drawn = [];
      reached = false;
      reason = '';
      summary = '';
      feedback = validation.message;
      feedbackType = 'error';
    } else if (reason === 'wish-missing') {
      drawn = [];
      reached = false;
      summary = `Für „${wish?.cardName || 'das Wunsch-Pokémon'}“ gibt es mit diesen Filtern keine passende Karte.`;
      feedback = summary;
      feedbackType = 'error';
    } else if (reason === 'empty' || (matchingCount === 0 && !edited)) {
      drawn = [];
      reached = false;
      summary = matchingMessage(0);
      feedback = summary;
      feedbackType = 'error';
    } else if (!drawn.length) {
      summary = 'Noch kein Los.';
      feedback = summary;
      feedbackType = 'error';
    } else if (!reached && !edited) {
      if (reason === 'wish') summary = `Die Wunschkarte liegt über dem Budget. Aktuell ${formatMoney(sum)}.`;
      else if (sizeMode === 'budget') summary = `Mit diesen Filtern kommen wir nur auf ${formatMoney(sum)} – ${loosenHint}`;
      else summary = matchingMessage(matchingCount < validation.count ? matchingCount : drawn.length);
      feedback = summary;
      feedbackType = 'error';
    } else if (!meetsMinimum(drawn, sum)) {
      summary = `${drawn.length} ${drawn.length === 1 ? 'Karte' : 'Karten'} im Los.`;
      feedback = `Ein Los braucht ${lotMinimumHint()}.`;
      feedbackType = 'error';
    } else {
      summary = `${drawn.length} ${drawn.length === 1 ? 'Karte' : 'Karten'} im Los.`;
      if (mixHints.length) {
        feedback = mixHints.join(' ');
        feedbackType = 'info';
      }
    }

    if (mixHints.length && feedbackType === 'error' && feedback && !mixHints.some((hint) => feedback.includes(hint))) {
      feedback = `${feedback} ${mixHints.join(' ')}`;
    } else if (mixHints.length && !feedback) {
      feedback = mixHints.join(' ');
      feedbackType = 'info';
    }

    if (wishInCart && wish && validation.ok && reason !== 'wish-missing') {
      const note = `„${wish.cardName}“ liegt bereits im Warenkorb und wird nicht erneut hinzugefügt.`;
      feedback = feedback ? `${note} ${feedback}` : note;
      if (feedbackType !== 'error') feedbackType = feedbackType || 'info';
    }

    const nodes = [];
    const shares = mixShares();
    if ((shares.length >= 2 || cardKindMode === 'mixed') && drawn.length) {
      const groups = new Map(shares.map((share) => [share.label, []]));
      drawn.forEach((card) => {
        const key =
          card.cardKind === 'trainer'
            ? 'Trainer'
            : groups.has(card.energyType)
              ? card.energyType
              : card.energyType || 'Gemischt';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(card);
      });
      let index = 0;
      groups.forEach((cards, label) => {
        if (!cards.length) return;
        const group = document.createElement('li');
        group.className = 'kl24-builder__group';
        const paint = energyPaint(label);
        group.style.setProperty('--energy', paint.color);
        const dot = document.createElement('span');
        dot.className = 'kl24-builder__group-dot';
        dot.setAttribute('aria-hidden', 'true');
        const text = document.createElement('span');
        text.textContent = `${label} · ${cards.length}`;
        group.append(dot, text);
        nodes.push(group);
        cards.forEach((card) => {
          nodes.push(renderCard(card, index));
          index += 1;
        });
      });
    } else {
      nodes.push(...drawn.map(renderCard));
    }
    listEl.replaceChildren(...nodes);
    const barCount = validation.ok ? drawn.length : 0;
    const barSep = summaryEl?.parentElement?.querySelector('[aria-hidden="true"]');
    if (sizeMode === 'budget' && validation.ok) {
      const budgetLabel = formatMoney(validation.budgetCents || 0);
      const sumLabel = formatMoney(drawn.length ? sum : 0);
      if (summaryEl) {
        summaryEl.textContent =
          barCount === 1
            ? `1 Karte · ${sumLabel} von ${budgetLabel} Budget`
            : `${barCount} Karten · ${sumLabel} von ${budgetLabel} Budget`;
      }
      if (priceEl) {
        priceEl.textContent = '';
        priceEl.hidden = true;
      }
      if (barSep) barSep.hidden = true;
    } else {
      if (summaryEl) summaryEl.textContent = barCount === 1 ? '1 Karte' : `${barCount} Karten`;
      if (priceEl) {
        priceEl.hidden = false;
        priceEl.textContent = formatMoney(validation.ok && drawn.length ? sum : 0);
        priceEl.removeAttribute('title');
      }
      if (barSep) barSep.hidden = false;
    }
    showFeedback(feedback, feedbackType);
    renderMix();
    const underBudget =
      sizeMode !== 'budget' || sum <= (validation.budgetCents || 0);
    const sizeReached =
      sizeMode === 'count'
        ? drawn.length >= (validation.count || 0)
        : underBudget && drawn.length > 0 && meetsMinimum(drawn, sum);
    // Enable whenever a valid full lot exists – mix hints must not block checkout.
    const allowed =
      validation.ok &&
      drawn.length > 0 &&
      meetsMinimum(drawn, sum) &&
      mixSumOk() &&
      underBudget &&
      (sizeReached || (edited && meetsMinimum(drawn, sum))) &&
      !adding;
    if (sizeReached) reached = true;
    const reasonEl = root.querySelector('[data-lot-add-reason]');
    let ctaReason = '';
    if (!allowed) {
      if (validation.ok && drawn.length && !meetsMinimum(drawn, sum)) {
        ctaReason = lotMinimumHint();
      } else if (!validation.ok && validation.message) {
        ctaReason = validation.message;
      } else if (!drawn.length) {
        ctaReason = 'Zuerst eine Auswahl laden';
      } else if (!mixSumOk()) {
        ctaReason = 'Energie-Anteile müssen 100 % ergeben';
      } else if (!underBudget) {
        ctaReason = 'Über dem Budget – Karten entfernen oder Budget erhöhen';
      }
    }
    if (addBtn) {
      addBtn.disabled = !allowed;
      addBtn.classList.toggle('is-disabled', !allowed);
      if (ctaReason) {
        addBtn.setAttribute('title', ctaReason);
        addBtn.setAttribute('aria-description', ctaReason);
      } else {
        addBtn.removeAttribute('title');
        addBtn.removeAttribute('aria-description');
      }
    }
    if (reasonEl) {
      reasonEl.hidden = !ctaReason;
      reasonEl.textContent = ctaReason;
    }
    syncBudgetHint();
    root.dataset.lotState = 'ready';
    root.dataset.drawGen = String(drawGen);
  };

  const pickOptions = () => ({
    noDupes: root.querySelector('[data-lot-dupes]')?.checked !== false,
    holoMode,
    reroll,
  });

  const trainerQuota = (total) => {
    if (cardKindMode !== 'mixed' || total <= 0) return 0;
    const parts = allocateParts(total, [TRAINER_SHARE_PERCENT, 100 - TRAINER_SHARE_PERCENT]);
    return parts[0] || 0;
  };

  const drawEnergyShares = (validation, pinnedWish, pokemonTarget, blocked, hints) => {
    const shares = mixShares();
    const picked = [];
    const opts = pickOptions();
    const countMode = validation.count > 0;
    const shortByLabel = new Map();

    if (shares.length < 2) {
      const energies = selectedEnergies();
      const guaranteed =
        pinnedWish && pinnedWish.cardKind !== 'trainer'
          ? [pinnedWish]
          : [];
      const pool = filteredPokemon({
        energyTypes: energies,
        blockedIds: blocked,
      });
      const result = poolApi.pickLot(pool, {
        ...opts,
        guaranteed,
        count: countMode ? pokemonTarget : 0,
        budgetCents: countMode ? 0 : pokemonTarget,
      });
      result.cards.forEach((card) => {
        picked.push(card);
        blocked.add(card.variantId);
      });
      return picked;
    }

    const targets = allocateParts(
      pokemonTarget,
      shares.map((share) => share.percent)
    );

    shares.forEach((share, index) => {
      const target = targets[index] || 0;
      if (target <= 0) return;
      const guaranteed =
        pinnedWish &&
        pinnedWish.cardKind !== 'trainer' &&
        String(pinnedWish.energyType || '') === share.label
          ? [pinnedWish]
          : [];
      const pool = filteredEnergy(share.label, blocked);
      const result = poolApi.pickLot(pool, {
        ...opts,
        guaranteed,
        count: countMode ? target : 0,
        budgetCents: countMode ? 0 : target,
      });
      result.cards.forEach((card) => {
        picked.push(card);
        blocked.add(card.variantId);
      });
      if (countMode) {
        if (result.cards.length < target) shortByLabel.set(share.label, { wanted: target, got: result.cards.length });
      } else {
        const got = result.cards.reduce((sum, card) => sum + card.priceCents, 0);
        if (got < target) shortByLabel.set(share.label, { wanted: target, got });
      }
    });

    let deficit = countMode
      ? Math.max(0, pokemonTarget - picked.length)
      : Math.max(
          0,
          pokemonTarget - picked.reduce((sum, card) => sum + card.priceCents, 0)
        );

    let filledFromOtherTypes = 0;

    if (deficit > 0) {
      const receivers = shares.filter((share) => !shortByLabel.has(share.label));
      const fillLabels = receivers.length ? receivers : shares;
      const fillParts = allocateParts(
        deficit,
        fillLabels.map((share) => share.percent || 1)
      );
      fillLabels.forEach((share, index) => {
        const part = fillParts[index] || 0;
        if (part <= 0) return;
        const result = poolApi.pickLot(filteredEnergy(share.label, blocked), {
          ...opts,
          guaranteed: [],
          count: countMode ? part : 0,
          budgetCents: countMode ? 0 : part,
        });
        result.cards.forEach((card) => {
          picked.push(card);
          blocked.add(card.variantId);
          filledFromOtherTypes += 1;
        });
      });
    }

    deficit = countMode
      ? Math.max(0, pokemonTarget - picked.length)
      : Math.max(
          0,
          pokemonTarget - picked.reduce((sum, card) => sum + card.priceCents, 0)
        );

    if (deficit > 0) {
      const result = poolApi.pickLot(
        filteredPokemon({
          energyTypes: shares.map((share) => share.label),
          blockedIds: blocked,
        }),
        {
          ...opts,
          guaranteed: [],
          count: countMode ? deficit : 0,
          budgetCents: countMode ? 0 : deficit,
        }
      );
      result.cards.forEach((card) => {
        picked.push(card);
        blocked.add(card.variantId);
        filledFromOtherTypes += 1;
      });
    }

    if (filledFromOtherTypes > 0 && shortByLabel.size) {
      shortByLabel.forEach((info, label) => {
        if (countMode) {
          hints.push(`Bei ${label} nur ${info.got} passende Karten – Rest mit anderen Typen aufgefüllt.`);
        } else {
          hints.push(`Bei ${label} nur ${formatMoney(info.got)} Bestand – Rest mit anderen Typen aufgefüllt.`);
        }
      });
    }

    return picked;
  };

  const drawMixed = (validation, pinnedWish) => {
    const hints = [];
    const blocked = new Set();
    const countMode = validation.count > 0;
    const totalTarget = countMode ? validation.count : validation.budgetCents;
    const trainerTarget = trainerQuota(totalTarget);
    const pokemonTarget = Math.max(0, totalTarget - trainerTarget);
    const opts = pickOptions();

    if (trainerTarget > 0) {
      hints.push(
        `Trainer max. ${TRAINER_SHARE_PERCENT} % des Loses – zählen nicht zur Energie-Mischung.`
      );
    }

    const pokemonCards = drawEnergyShares(validation, pinnedWish, pokemonTarget, blocked, hints);
    const trainerGuaranteed =
      pinnedWish && pinnedWish.cardKind === 'trainer' ? [pinnedWish] : [];
    const trainerResult = poolApi.pickLot(filteredTrainers(blocked), {
      ...opts,
      guaranteed: trainerGuaranteed,
      count: countMode ? trainerTarget : 0,
      budgetCents: countMode ? 0 : trainerTarget,
    });
    trainerResult.cards.forEach((card) => blocked.add(card.variantId));

    let picked = [...pokemonCards, ...trainerResult.cards];

    let deficit = countMode
      ? Math.max(0, totalTarget - picked.length)
      : Math.max(0, totalTarget - picked.reduce((sum, card) => sum + card.priceCents, 0));

    if (deficit > 0) {
      const fillPool = filteredPokemon({
        energyTypes: selectedEnergies(),
        blockedIds: blocked,
      });
      const fill = poolApi.pickLot(fillPool, {
        ...opts,
        guaranteed: [],
        count: countMode ? deficit : 0,
        budgetCents: countMode ? 0 : deficit,
      });
      fill.cards.forEach((card) => {
        picked.push(card);
        blocked.add(card.variantId);
      });
    }

    if (pinnedWish && !picked.some((card) => card.variantId === pinnedWish.variantId)) {
      const wishPrice = Number(pinnedWish.priceCents) || 0;
      if (countMode || wishPrice <= validation.budgetCents) {
        picked.unshift(pinnedWish);
        if (countMode && picked.length > validation.count) picked.length = validation.count;
      }
    }

    // Keep exact target size in count mode (drop overflow after fills/wish pin).
    if (countMode && picked.length > validation.count) picked.length = validation.count;

    // Hard budget cap: drop trailing cards until under budget (keep wish if possible).
    if (!countMode) {
      let sumCheck = picked.reduce((total, card) => total + (card.priceCents || 0), 0);
      while (picked.length && sumCheck > validation.budgetCents) {
        const dropIdx =
          pinnedWish && picked[0]?.variantId === pinnedWish.variantId && picked.length > 1
            ? picked.length - 1
            : picked.length - 1;
        const [removed] = picked.splice(dropIdx, 1);
        sumCheck -= removed?.priceCents || 0;
      }
    }

    const sum = picked.reduce((total, card) => total + card.priceCents, 0);
    const wishOver =
      Boolean(pinnedWish) && (Number(pinnedWish.priceCents) || 0) > validation.budgetCents;
    const reachedMixed = countMode
      ? picked.length >= validation.count
      : picked.length > 0 && sum <= validation.budgetCents && !wishOver;
    mixHints = hints;
    return {
      cards: picked,
      reached: reachedMixed,
      reason: reachedMixed ? '' : wishOver ? 'wish' : 'short',
    };
  };

  const setRollingUi = (on) => {
    root.classList.toggle('is-rolling', on);
    root.dataset.lotState = on ? 'rolling' : root.dataset.lotState || 'ready';
    listEl?.classList.toggle('is-dimmed', on);
    if (rollBtn) {
      // Keep enabled so queued taps can register; handler gates on rolling.
      rollBtn.disabled = false;
      rollBtn.classList.toggle('is-loading', on);
      if (on) {
        rollBtn.setAttribute('aria-busy', 'true');
        if (!rollBtn.dataset.labelIdle) rollBtn.dataset.labelIdle = rollBtn.textContent || '';
        rollBtn.textContent = 'Würfle …';
      } else {
        rollBtn.removeAttribute('aria-busy');
        if (rollBtn.dataset.labelIdle) rollBtn.textContent = rollBtn.dataset.labelIdle;
      }
    }
  };

  const setPendingSizeUi = () => {
    // Immediately reflect the new target size in the summary (old count vanishes).
    if (!summaryEl) return;
    if (sizeMode === 'count') {
      const n = currentCount();
      summaryEl.textContent = n ? `${n} Karten · wird gewürfelt …` : 'Wird gewürfelt …';
    } else {
      const b = currentBudget();
      summaryEl.textContent = b
        ? `Budget ${formatMoney(b)} · wird gewürfelt …`
        : 'Budget · wird gewürfelt …';
    }
    root.dataset.lotState = 'pending';
    listEl?.classList.add('is-dimmed');
    if (addBtn && !adding) addBtn.disabled = true;
  };

  const flushPendingActions = () => {
    if (pendingRoll) {
      pendingRoll = false;
      rolling = true;
      reroll = true;
      drawGen += 1;
      window.clearTimeout(timer);
      if (idleHandle && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      idleHandle = 0;
      setRollingUi(true);
      draw();
      return;
    }
    if (pendingAdd) {
      pendingAdd = false;
      addLot();
    }
  };

  const draw = async () => {
    if (!ready) return;
    const gen = drawGen;
    const started = performance.now();
    try {
      await syncCartBlocklist();
      if (gen !== drawGen) return;

      const validation = validate();
      const cards = filtered();
      matchingCount = cards.length;
      mixHints = [];
      renderPoolCount();
      renderMix();
      edited = false;
      if (!validation.ok) {
        drawn = [];
        reached = false;
        reason = '';
        wishInCart = false;
        render();
        return;
      }

      wishInCart = Boolean(wish && cartVariantIds.has(String(wish.variantId)));
      const pinnedWish = wishInCart ? null : resolvePinnedWish(cards);
      if (wish && !wishInCart && !pinnedWish) {
        drawn = [];
        reached = false;
        reason = 'wish-missing';
        render();
        return;
      }
      if (!matchingCount) {
        drawn = [];
        reached = false;
        reason = 'empty';
        render();
        return;
      }

      let result;
      if (cardKindMode === 'mixed' || (cardKindMode === 'pokemon' && mixShares().length >= 2)) {
        result = drawMixed(validation, pinnedWish);
      } else {
        const wishOverBudget =
          sizeMode === 'budget' &&
          pinnedWish &&
          (Number(pinnedWish.priceCents) || 0) > validation.budgetCents;
        if (wishOverBudget) {
          result = { cards: [], reached: false, reason: 'wish' };
        } else {
          result = poolApi.pickLot(cards, {
            ...pickOptions(),
            guaranteed: pinnedWish ? [pinnedWish] : [],
            count: validation.count,
            budgetCents: validation.budgetCents,
          });
          if (pinnedWish && !result.cards.some((card) => card.variantId === pinnedWish.variantId)) {
            const wishPrice = Number(pinnedWish.priceCents) || 0;
            const currentSum = result.cards.reduce((total, card) => total + (card.priceCents || 0), 0);
            if (
              validation.count ||
              currentSum + wishPrice <= validation.budgetCents
            ) {
              result.cards.unshift(pinnedWish);
              if (validation.count && result.cards.length > validation.count) {
                result.cards.length = validation.count;
              }
              if (!validation.count) {
                let sumCheck = result.cards.reduce((total, card) => total + (card.priceCents || 0), 0);
                while (result.cards.length && sumCheck > validation.budgetCents) {
                  const removed = result.cards.pop();
                  sumCheck -= removed?.priceCents || 0;
                }
              }
              result.reached = validation.count
                ? result.cards.length >= validation.count
                : result.cards.length > 0;
            }
          }
        }
      }

      reroll = false;
      const targetCount = sizeMode === 'count' ? validation.count : 0;
      const reservedScrub = await ensureReservableLot(result.cards, { targetCount });
      if (gen !== drawGen) return;
      drawn = reservedScrub.cards;

      // Dry-run / QA only: data-kl24-budget-stress="1" simulates a pricey scrub
      // replacement (+6¢) so the re-trim below is exercised every draw.
      if (
        root.dataset.kl24BudgetStress === '1' &&
        sizeMode === 'budget' &&
        drawn.length
      ) {
        const last = drawn[drawn.length - 1];
        if (last) last.priceCents = (Number(last.priceCents) || 0) + 6;
      }

      // Budget mode: replacements from ensureReservableLot can push sum over budget.
      // Re-trim after every scrub so ATC stays enabled only when sum <= budget.
      if (sizeMode === 'budget' && validation.budgetCents > 0 && drawn.length) {
        let sumCheck = drawn.reduce((total, card) => total + (Number(card.priceCents) || 0), 0);
        const wishId = pinnedWish ? String(pinnedWish.variantId) : '';
        while (drawn.length && sumCheck > validation.budgetCents) {
          let dropIdx = drawn.length - 1;
          if (wishId && String(drawn[dropIdx]?.variantId) === wishId && drawn.length > 1) {
            dropIdx = drawn.length - 2;
          }
          const [removed] = drawn.splice(dropIdx, 1);
          sumCheck -= Number(removed?.priceCents) || 0;
        }
      }

      const drawnSum = drawn.reduce((total, card) => total + (Number(card.priceCents) || 0), 0);
      const wishOverBudget =
        Boolean(pinnedWish) && (Number(pinnedWish.priceCents) || 0) > validation.budgetCents;
      reached =
        sizeMode === 'count'
          ? drawn.length >= validation.count
          : drawn.length > 0 && drawnSum <= validation.budgetCents && !wishOverBudget;
      if (reached) reason = '';
      else if (result.reason === 'wish' || wishOverBudget) reason = 'wish';
      else if (sizeMode === 'count' && matchingCount < validation.count) reason = 'short';
      else reason = 'short';
      if (gen !== drawGen) return;
      root.dataset.kl24DrawSeq = String((Number(root.dataset.kl24DrawSeq) || 0) + 1);
      render();
    } catch (error) {
      console.warn('[Wunsch-Los] draw failed', error);
      render();
    } finally {
      root.dataset.calcMs = String(Math.round(performance.now() - started));
      if (gen === drawGen) {
        rolling = false;
        setRollingUi(false);
        flushPendingActions();
      }
    }
  };

  const resolvePinnedWish = (pool) => {
    if (!wish) return null;
    const exact = pool.find((card) => card.variantId === wish.variantId);
    if (exact) return exact;
    const name = String(wish.cardName || '').trim().toLowerCase();
    if (!name) return null;
    return pool.find((card) => String(card.cardName || '').trim().toLowerCase() === name) || null;
  };

  const cancelScheduledDraw = () => {
    drawGen += 1;
    window.clearTimeout(timer);
    if (idleHandle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
    idleHandle = 0;
  };

  const drawNow = () => {
    cancelScheduledDraw();
    draw();
  };

  const scheduleDraw = () => {
    drawGen += 1;
    const gen = drawGen;
    window.clearTimeout(timer);
    if (idleHandle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
    idleHandle = 0;
    setPendingSizeUi();
    timer = window.setTimeout(() => {
      const run = () => {
        idleHandle = 0;
        if (gen !== drawGen) return;
        rolling = true;
        setRollingUi(true);
        draw();
      };
      if (typeof window.requestIdleCallback === 'function') idleHandle = window.requestIdleCallback(run, { timeout: 60 });
      else run();
    }, 200);
  };

  const cardLabel = (card) => `${card.cardName}|${card.setCode}|${card.cardNumber}|${card.variantId}`;

  const swapCard = (variantId) => {
    cancelScheduledDraw();
    const index = drawn.findIndex((card) => card.variantId === variantId);
    if (index < 0) return;
    const current = drawn[index];
    const noDupes = root.querySelector('[data-lot-dupes]')?.checked !== false;
    const usedIds = new Set(drawn.map((card) => card.variantId));
    const usedKeys = new Set(
      drawn.filter((card) => card.variantId !== current.variantId).map((card) => poolApi.dedupeKey(card))
    );
    const kind = current.cardKind || 'pokemon';
    const sameTypePool =
      kind === 'trainer'
        ? filteredTrainers()
        : current.energyType
          ? filteredEnergy(current.energyType)
          : filteredPokemon({ energyTypes: selectedEnergies() });
    const options = sameTypePool.filter((card) => {
      if (usedIds.has(card.variantId)) return false;
      if ((card.cardKind || 'pokemon') !== kind) return false;
      if (noDupes && usedKeys.has(poolApi.dedupeKey(card))) return false;
      if (kind !== 'trainer' && current.energyType && card.energyType !== current.energyType) return false;
      return cardLabel(card) !== cardLabel(current);
    });
    const limit = Math.max(50, Math.round(current.priceCents * 0.5));
    const close = options.filter((card) => Math.abs(card.priceCents - current.priceCents) <= limit);
    const pickFrom = close.length ? close : options;
    if (!pickFrom.length) {
      const missLabel =
        kind === 'trainer'
          ? 'Keine passende Trainer-Alternative'
          : current.energyType
            ? `Keine passende Alternative vom Typ ${current.energyType}`
            : 'Keine passende Alternative';
      showSwapMiss(variantId, missLabel);
      return;
    }
    pickFrom.sort(
      (a, b) =>
        Math.abs(a.priceCents - current.priceCents) - Math.abs(b.priceCents - current.priceCents) ||
        String(a.variantId).localeCompare(String(b.variantId))
    );
    const bestGap = Math.abs(pickFrom[0].priceCents - current.priceCents);
    let band = pickFrom.filter((card) => Math.abs(card.priceCents - current.priceCents) <= bestGap + 15);
    // Budget mode: never swap to a card that would push the lot over budget.
    if (sizeMode === 'budget') {
      const budgetCents = currentBudget();
      if (budgetCents > 0) {
        const othersSum = drawn.reduce(
          (total, card, i) => (i === index ? total : total + (Number(card.priceCents) || 0)),
          0
        );
        const underBudget = band.filter(
          (card) => othersSum + (Number(card.priceCents) || 0) <= budgetCents
        );
        if (underBudget.length) band = underBudget;
        else {
          showSwapMiss(variantId, 'Keine Alternative unter Budget');
          return;
        }
      }
    }
    const replacement = band[Math.floor(Math.random() * band.length)];
    drawn[index] = replacement;
    if (wish && wish.variantId === current.variantId) wish = replacement;
    edited = true;
    reached = true;
    render();
  };

  const showSwapMiss = (variantId, message) => {
    listEl?.querySelector('[data-swap-miss]')?.remove();
    const item = listEl?.querySelector(`[data-variant-id="${CSS.escape(String(variantId))}"]`);
    if (!item) return;
    const note = document.createElement('p');
    note.className = 'kl24-builder__notice is-error';
    note.dataset.swapMiss = 'true';
    note.setAttribute('role', 'status');
    note.textContent = message;
    item.append(note);
  };

  const removeCard = (variantId) => {
    if (wish && wish.variantId === variantId) wish = null;
    drawn = drawn.filter((card) => card.variantId !== variantId);
    edited = true;
    reached = true;
    renderWish();
    render();
  };

  const mixText = () => {
    const shares = mixShares();
    if (shares.length < 2) return '';
    return shares.map((share) => `${share.label} ${share.percent} %`).join(' / ');
  };

  const lotLabel = () => {
    if (cardKindMode === 'trainer') return `Wunsch-Los Trainer · ${drawn.length} Karten`;
    const hasTrainers = drawn.some((card) => card.cardKind === 'trainer');
    const mix = mixText();
    let title = mix;
    if (!title) {
      const names = [
        ...new Set(
          drawn
            .filter((card) => card.cardKind !== 'trainer')
            .map((card) => card.energyType)
            .filter(Boolean)
        ),
      ];
      title = names.length > 0 && names.length <= 3 ? names.join(', ') : 'Gemischt';
    }
    return `Wunsch-Los ${title}${hasTrainers ? ' + Trainer' : ''} · ${drawn.length} Karten`;
  };

  const packList = (items) => {
    const groups = new Map();
    items.forEach((item) => {
      const props = item.properties || {};
      const id = props._wunsch_los;
      if (!id) return;
      if (!groups.has(id)) {
        groups.set(id, { label: props._wunsch_los_label || 'Wunsch-Los', rows: [] });
      }
      groups.get(id).rows.push({
        set: props._wunsch_los_set || '',
        number: props._wunsch_los_num || '',
        name: props._wunsch_los_name || item.product_title || item.title || '',
        price: formatMoney(item.final_line_price || item.price || 0),
      });
    });
    const chunks = [];
    groups.forEach((group) => {
      group.rows.sort((a, b) => a.set.localeCompare(b.set, 'de') || a.number.localeCompare(b.number, 'de', { numeric: true }));
      chunks.push([group.label, ...group.rows.map((row) => `${row.set} ${row.number} ${row.name} ${row.price}`.replace(/\s+/g, ' ').trim())].join('\n'));
    });
    const text = chunks.join('\n\n');
    return text.length > 4500 ? `${text.slice(0, 4480)}\n… weitere Karten stehen an den Positionen` : text;
  };

  const refreshDrawer = (parsed) => {
    try {
      const html = parsed?.sections?.['cart-drawer'];
      const drawer = document.querySelector('cart-drawer');
      if (!html || !drawer || typeof drawer.getSectionInnerHTML !== 'function') return;
      const target = document.querySelector('#CartDrawer');
      const inner = target ? drawer.getSectionInnerHTML(html, '#CartDrawer') : '';
      if (!target || !inner) return;
      target.innerHTML = inner;
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
    } catch (error) {
      console.warn('[Wunsch-Los] Warenkorb', error);
    }
  };

  const cardMentioned = (card, message) => {
    const hay = String(message || '').toLowerCase();
    const name = String(card.cardName || '').toLowerCase();
    if (name.length > 2 && hay.includes(name)) return true;
    const title = String(card.title || '').toLowerCase();
    if (title.length > 2 && hay.includes(title)) return true;
    const sku = String(card.sku || '').toLowerCase();
    return Boolean(sku && hay.includes(sku));
  };

  const postAddBatch = async (cards, properties) => {
    const items = cards.map((card) => ({
      id: Number(card.variantId),
      quantity: 1,
      properties: typeof properties === 'function' ? properties(card) : properties || undefined,
    }));
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items,
        sections: CART_SECTIONS,
        sections_url: window.location.pathname,
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(body.description || body.message || 'Warenkorb konnte nicht aktualisiert werden.');
      err.status = response.status;
      err.description = body.description || '';
      err.stock = response.status === 422;
      throw err;
    }
    return body;
  };

  const openPoolForReplace = (usedIds, heldIds) => {
    const blocked = new Set(cartVariantIds);
    (usedIds || []).forEach((id) => {
      if (id != null && id !== '') blocked.add(String(id));
    });
    (heldIds || []).forEach((id) => {
      if (id != null && id !== '') blocked.add(String(id));
    });
    return baseFilter({ blockedIds: blocked });
  };

  const scrubCartDupes = (cards) => {
    let cartReplaced = 0;
    const next = [];
    const used = new Set();
    for (const card of cards) {
      const id = String(card.variantId);
      if (!cartVariantIds.has(id) && !used.has(id)) {
        next.push(card);
        used.add(id);
        continue;
      }
      const fromCart = cartVariantIds.has(id);
      const pool = openPoolForReplace(used);
      const rep = poolApi.replacementFor(card, pool, used);
      if (fromCart) cartReplaced += 1;
      if (!rep) continue;
      next.push(rep);
      used.add(String(rep.variantId));
    }
    return { cards: next, cartReplaced };
  };

  const replaceCardsInBatch = (batch, targets) => {
    let cartReplaced = 0;
    let next = batch.slice();
    const used = new Set(next.map((card) => String(card.variantId)));
    for (const card of targets) {
      const fromCart = cartVariantIds.has(String(card.variantId));
      used.delete(String(card.variantId));
      const pool = openPoolForReplace(used);
      const rep = poolApi.replacementFor(card, pool, used);
      if (fromCart) cartReplaced += 1;
      if (rep) {
        next = next.map((entry) => (String(entry.variantId) === String(card.variantId) ? rep : entry));
        used.add(String(rep.variantId));
      } else {
        next = next.filter((entry) => String(entry.variantId) !== String(card.variantId));
      }
    }
    return { batch: next, cartReplaced };
  };

  const identifyFailed = async (batch, error) => {
    const message = `${error?.description || ''} ${error?.message || ''}`;
    const mentioned = batch.filter((card) => cardMentioned(card, message));
    if (mentioned.length) return { cards: mentioned, fromCart: /warenkorb|cart|quantity|menge/i.test(message) };
    await syncCartBlocklist();
    const inCart = batch.filter((card) => cartVariantIds.has(String(card.variantId)));
    if (inCart.length) return { cards: inCart, fromCart: true };
    for (const card of batch) {
      if (!card.handle) continue;
      try {
        const res = await fetch(`/products/${encodeURIComponent(card.handle)}.js`, {
          credentials: 'same-origin',
        });
        if (!res.ok) continue;
        const product = await res.json();
        const variant = (product.variants || []).find((entry) => String(entry.id) === String(card.variantId));
        if (variant && variant.available === false) return { cards: [card], fromCart: false };
      } catch {
        /* keep looking */
      }
    }
    return null;
  };

  const addSinglesWithReplace = async (cards, properties) => {
    const added = [];
    let cartReplaced = 0;
    let lastParsed = null;
    const queue = cards.slice();
    let guard = 0;
    const maxGuards = Math.max(24, cards.length * 3);
    while (queue.length && guard < maxGuards) {
      guard += 1;
      const card = queue.shift();
      if (!card) break;
      if (cartVariantIds.has(String(card.variantId))) {
        const used = new Set([...added, ...queue].map((entry) => String(entry.variantId)));
        const pool = openPoolForReplace(used);
        const rep = poolApi.replacementFor(card, pool, used);
        cartReplaced += 1;
        if (rep) queue.push(rep);
        continue;
      }
      try {
        lastParsed = await postAddBatch([card], properties);
        added.push(card);
        cartVariantIds.add(String(card.variantId));
      } catch (error) {
        if (error.status !== 422 && !error.stock) throw error;
        const used = new Set([...added, ...queue].map((entry) => String(entry.variantId)));
        used.add(String(card.variantId));
        const pool = openPoolForReplace(used);
        const rep = poolApi.replacementFor(card, pool, used);
        const msg = `${error?.description || ''} ${error?.message || ''}`;
        if (/warenkorb|cart|quantity|menge/i.test(msg)) cartReplaced += 1;
        if (rep) queue.push(rep);
      }
    }
    return { added, cartReplaced, lastParsed };
  };

  const addWithRetry = async (cards, properties) => {
    let cartReplaced = 0;
    const pending = cards.slice();
    const added = [];
    let lastParsed = null;

    while (pending.length) {
      let current = pending.splice(0, 50);
      let placed = false;
      for (let attempt = 0; attempt < 8 && !placed; attempt += 1) {
        try {
          lastParsed = await postAddBatch(current, properties);
          added.push(...current);
          current.forEach((card) => cartVariantIds.add(String(card.variantId)));
          placed = true;
        } catch (error) {
          if (error.status !== 422 && !error.stock) throw error;
          const failed = await identifyFailed(current, error);
          if (!failed || attempt >= 6) {
            const singles = await addSinglesWithReplace(current, properties);
            added.push(...singles.added);
            cartReplaced += singles.cartReplaced;
            if (singles.lastParsed) lastParsed = singles.lastParsed;
            placed = true;
            break;
          }
          if (failed.fromCart) {
            failed.cards.forEach((card) => cartVariantIds.add(String(card.variantId)));
          }
          const repaired = replaceCardsInBatch(current, failed.cards);
          cartReplaced += repaired.cartReplaced;
          current = repaired.batch;
          if (!current.length) placed = true;
        }
      }
    }
    return { added, cartReplaced, parsed: lastParsed };
  };

  const goToCartPage = async () => {
    // Fresh cart.js (no-store) after every /cart/add.js so /cart is never empty/stale.
    for (let attempt = 0; attempt < 6; attempt += 1) {
      await syncCartBlocklist();
      if ((latestCart?.item_count || 0) > 0) break;
      await new Promise((resolve) => window.setTimeout(resolve, 100 + attempt * 80));
    }
    window.location.assign('/cart');
  };

  const addLot = async () => {
    if (adding || addBtn?.disabled || !drawn.length) return;
    adding = true;
    let addedOk = false;
    let addedCount = 0;
    let expectedCount = drawn.length;
    let cartReplacedCount = 0;
    let cartError = '';
    let successMessage = '';
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');
      addBtn.textContent = 'Wird in den Warenkorb gelegt …';
    }
    showFeedback('', '');
    const lotId = `wl-${Date.now().toString(36)}`;
    const label = lotLabel();
    try {
      await syncCartBlocklist();
      const scrubbed = scrubCartDupes(drawn);
      // Only real cart-dupes count for the „lagen schon im Warenkorb“ message.
      cartReplacedCount = scrubbed.cartReplaced;
      const reservedScrub = await ensureReservableLot(scrubbed.cards, {
        targetCount: sizeMode === 'count' ? currentCount() : 0,
        force: true,
      });
      const reserveReplacedCount = reservedScrub.replaced;
      const working = reservedScrub.cards;
      if (!working.length) {
        throw new Error(
          reserveReplacedCount || scrubbed.cartReplaced
            ? 'Die gezogenen Karten sind gerade bei anderen Kunden reserviert oder schon im Warenkorb. Bitte neue Auswahl laden.'
            : 'Keine freien Karten für dieses Los. Bitte neue Auswahl laden.'
        );
      }
      if (scrubbed.cartReplaced || reserveReplacedCount) {
        drawn = working;
        render();
        if (addBtn) {
          addBtn.disabled = true;
          addBtn.textContent = 'Wird in den Warenkorb gelegt …';
        }
      }
      expectedCount = working.length;
      const germanSetName = (raw) =>
        String(raw || '')
          .replace(/\bPitch Black\b/g, 'Dunkelnacht')
          .replace(/\bObsidian Flames\b/g, 'Obsidianflammen');
      const properties = (card) => {
        const props = {
          _wunsch_los: lotId,
          _wunsch_los_label: label,
          _wunsch_los_set: germanSetName(card.setName || card.setCode || ''),
          _wunsch_los_num: card.cardNumber || '',
          _wunsch_los_name: card.cardName || '',
        };
        const mix = mixText();
        if (mix) props._wunsch_los_mix = mix;
        return props;
      };

      // Await /cart/add.js fully (incl. retries) before chrome refresh + redirect.
      const result = await addWithRetry(working, properties);
      cartReplacedCount += result.cartReplaced;
      await syncCartBlocklist();
      const cart = latestCart;
      const lotItems = (cart?.items || []).filter(
        (item) => item.properties && String(item.properties._wunsch_los || '') === lotId
      );
      addedCount = lotItems.length;

      if (!addedCount) {
        throw new Error('Die Karten konnten nicht in den Warenkorb gelegt werden. Bitte versuche es noch einmal.');
      }

      if (cart) {
        await poolApi.updateAttributes({ 'Wunsch-Los': packList(cart.items || []) });
        window.Kl24CartEnhancements?.syncCartState?.(cart);
      }
      await refreshCartChrome(result.parsed);

      const parts = [];
      if (cartReplacedCount > 0) {
        parts.push(`${cartReplacedCount} Karten lagen schon in deinem Warenkorb und wurden ersetzt.`);
      }
      if (reserveReplacedCount > 0) {
        parts.push(
          `${reserveReplacedCount} ${
            reserveReplacedCount === 1 ? 'Karte war' : 'Karten waren'
          } gerade reserviert und ${reserveReplacedCount === 1 ? 'wurde' : 'wurden'} ersetzt.`
        );
      }
      if (addedCount !== expectedCount) {
        parts.push(`Im Warenkorb liegen ${addedCount} statt ${expectedCount} Karten aus diesem Los.`);
      } else {
        parts.push(`${addedCount} Karten liegen im Warenkorb.`);
      }
      successMessage = parts.join(' ');
      addedOk = true;
      drawn = [];
      wish = null;
      wishInCart = false;
      reached = false;
      reason = '';
      edited = false;
      renderWish();
      showFeedback(successMessage || `${addedCount} Karten liegen im Warenkorb.`, 'success');
      await goToCartPage();
      return;
    } catch (error) {
      cartError = error?.message || 'Der Warenkorb konnte nicht aktualisiert werden.';
    } finally {
      // Keep button locked on success until navigation; only unlock on error.
      if (!addedOk) {
        adding = false;
        if (addBtn) {
          addBtn.removeAttribute('aria-busy');
          addBtn.textContent = 'Los in den Warenkorb';
        }
        render();
        if (cartError) showFeedback(cartError, 'error');
      }
    }
  };

  const renderSets = () => {
    if (!setsEl) return;
    const counts = new Map();
    catalog.forEach((card) => {
      if (!card.setCode) return;
      const current = counts.get(card.setCode) || { name: card.setName || card.setCode, count: 0 };
      current.count += 1;
      counts.set(card.setCode, current);
    });
    const rows = [...counts.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name, 'de'));
    setsEl.replaceChildren();
    rows.forEach(([code, info]) => {
      const label = document.createElement('label');
      label.className = 'kl24-builder__check';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.value = code;
      input.dataset.setCount = String(info.count);
      input.addEventListener('change', scheduleDraw);
      const text = document.createElement('span');
      text.textContent = `${info.name} (${info.count})`;
      label.append(input, text);
      setsEl.append(label);
    });
  };

  const MIN_ENERGY_STOCK = 10;

  const languageMatches = (card) => {
    if (poolApi.languageMatchesCriteria) {
      return poolApi.languageMatchesCriteria(card.language, language);
    }
    // Fallback if pool API is older than this page bundle
    if (!language) return true;
    const lang = String(card.language || '').toLowerCase().trim() || 'deutsch';
    if (language === 'de') return lang === 'de' || lang.startsWith('deutsch') || lang === 'ger';
    if (language === 'en') return lang === 'en' || lang.startsWith('engl') || lang === 'eng';
    return lang === language.toLowerCase();
  };

  const energyStockCount = (label) =>
    catalog.filter((card) => {
      if (card.energyType !== label) return false;
      if ((card.cardKind || 'pokemon') === 'trainer') return false;
      return languageMatches(card);
    }).length;

  const onEnergyClick = (button) => {
    const label = button.dataset.lotEnergy || '';
    const active = button.getAttribute('aria-pressed') === 'true';
    if (!active && mixOrder.length >= MAX_ENERGY_TYPES) {
      showFeedback(`Maximal ${MAX_ENERGY_TYPES} Energietypen gleichzeitig.`, 'error');
      return;
    }
    const next = !active;
    button.setAttribute('aria-pressed', next ? 'true' : 'false');
    button.classList.toggle('is-active', next);
    if (next) {
      if (!mixOrder.includes(label)) mixOrder.push(label);
    } else {
      mixOrder = mixOrder.filter((entry) => entry !== label);
    }
    if (mixOrder.length >= 2) applyEqualMix(mixOrder);
    else mixPercents = new Map();
    syncLotEnergy();
    renderMix();
    scheduleDraw();
  };

  const renderEnergy = () => {
    const host = root.querySelector('[data-lot-energies]');
    if (!host) return;
    // Chips are SSR'd in Liquid; bind once and only sync pressed/disabled state.
    if (!host.dataset.lotEnergyBound) {
      host.dataset.lotEnergyBound = '1';
      host.querySelectorAll('[data-lot-energy]').forEach((button) => {
        button.addEventListener('click', () => onEnergyClick(button));
      });
    }
    const prevActive = new Set(mixOrder);
    host.querySelectorAll('[data-lot-energy]').forEach((button) => {
      const label = button.dataset.lotEnergy || '';
      const inStock = energyStockCount(label) >= MIN_ENERGY_STOCK;
      const wasActive = prevActive.has(label) && inStock;
      button.hidden = false;
      button.disabled = !inStock;
      button.setAttribute('aria-pressed', wasActive ? 'true' : 'false');
      button.classList.toggle('is-active', wasActive);
      button.classList.toggle('is-low-stock', !inStock);
      if (!inStock) {
        mixOrder = mixOrder.filter((entry) => entry !== label);
      }
    });
    mixOrder = mixOrder.filter((label) => energyStockCount(label) >= MIN_ENERGY_STOCK);
    if (mixOrder.length < 2) mixPercents = new Map();
    else applyEqualMix(mixOrder);
    syncLotEnergy();
    renderMix();
    syncCardKindUi();
  };

  const wishSearchPool = () => {
    const energies = selectedEnergies();
    return poolApi.filter(catalog, {
      nmOnly: true,
      language,
      setCodes: selectedSets(),
      holoMode: holoMode === 'none' ? 'none' : '',
      cardKinds: cardKindsForMode(),
      energyTypes: cardKindMode === 'trainer' ? [] : energies,
    });
  };

  const searchWish = () => {
    if (!wishResults || !wishSearch) return;
    const query = wishSearch.value.trim().toLowerCase();
    wishResults.replaceChildren();
    if (query.length < 2) {
      wishResults.hidden = true;
      return;
    }
    const hits = wishSearchPool()
      .filter((card) => `${card.cardName} ${card.title}`.toLowerCase().includes(query))
      .slice(0, 8);
    wishResults.hidden = false;
    if (!hits.length) {
      const empty = document.createElement('li');
      empty.textContent = 'Keine Karte gefunden.';
      wishResults.append(empty);
      return;
    }
    hits.forEach((card) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      const inCart = cartVariantIds.has(String(card.variantId));
      button.textContent = inCart ? `${formatCardLine(card)} — liegt bereits im Warenkorb` : formatCardLine(card);
      button.addEventListener('click', () => {
        wish = card;
        wishInCart = cartVariantIds.has(String(card.variantId));
        wishSearch.value = '';
        wishResults.hidden = true;
        renderWish();
        scheduleDraw();
      });
      item.append(button);
      wishResults.append(item);
    });
  };

  const bind = () => {
    renderEnergy();
    syncCardKindUi();
    root.querySelector('[data-lot-mix-equal]')?.addEventListener('click', () => {
      if (mixOrder.length < 2) return;
      applyEqualMix(mixOrder);
      renderMix();
      scheduleDraw();
    });
    root.querySelector('[data-lot-language]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-language]');
      if (!button) return;
      language = button.dataset.language || '';
      root.querySelectorAll('[data-language]').forEach((chip) => {
        const active = chip === button;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      renderEnergy();
      scheduleDraw();
    });
    root.querySelector('[data-lot-holo]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-holo]');
      if (!button) return;
      holoMode = button.dataset.holo || 'some';
      root.querySelectorAll('[data-holo]').forEach((chip) => {
        const active = chip === button;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      scheduleDraw();
    });
    root.querySelector('[data-lot-card-kind]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-card-kind]');
      if (!button) return;
      cardKindMode = button.dataset.cardKind || 'pokemon';
      root.querySelectorAll('[data-card-kind]').forEach((chip) => {
        const active = chip === button;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      syncCardKindUi();
      renderEnergy();
      scheduleDraw();
    });
    root.querySelector('[data-lot-size]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-size]');
      if (!button) return;
      rememberSize();
      sizeMode = button.dataset.size || 'count';
      root.querySelectorAll('[data-size]').forEach((chip) => {
        const active = chip === button;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      root.querySelector('[data-lot-count-wrap]').hidden = sizeMode !== 'count';
      root.querySelector('[data-lot-budget-wrap]').hidden = sizeMode !== 'budget';
      if (sizeMode === 'budget') {
        root.querySelectorAll('[data-count]').forEach((chip) => {
          chip.classList.remove('is-active');
          chip.setAttribute('aria-pressed', 'false');
        });
      }
      if (sizeMode === 'budget') {
        restoreBudget();
        syncBudgetHint();
      } else if (countPreset === 'custom') restoreCustomCount();
      scheduleDraw();
    });
    root.querySelector('[data-lot-counts]')?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-count]');
      if (!button) return;
      rememberSize();
      sizeMode = 'count';
      const countWrap = root.querySelector('[data-lot-count-wrap]');
      const budgetWrap = root.querySelector('[data-lot-budget-wrap]');
      if (countWrap) countWrap.hidden = false;
      if (budgetWrap) budgetWrap.hidden = true;
      root.querySelectorAll('[data-size]').forEach((chip) => {
        chip.classList.remove('is-active');
        chip.setAttribute('aria-pressed', 'false');
      });
      countPreset = button.dataset.count === 'custom' ? 'custom' : Number(button.dataset.count);
      root.querySelectorAll('[data-count]').forEach((chip) => {
        const active = chip === button;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      syncBuilderBarClearance();
      try {
        button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } catch {
        /* ignore */
      }
      if (countPreset === 'custom') restoreCustomCount();
      else if (countInput) countInput.hidden = true;
      scheduleDraw();
    });
    countInput?.addEventListener('focus', () => {
      if (countPreset === 'custom' && countInput.value) countInput.select();
    });
    countInput?.addEventListener('input', () => {
      // Value is taken only from the field (never concatenated with a preset).
      if (countPreset === 'custom') drawNow();
    });
    budgetInput?.addEventListener('input', drawNow);
    syncBuilderBarClearance();
    window.addEventListener('resize', syncBuilderBarClearance, { passive: true });
    if (typeof ResizeObserver === 'function') {
      const bar = document.querySelector('.kl24-builder__bar');
      if (bar) {
        const ro = new ResizeObserver(() => syncBuilderBarClearance());
        ro.observe(bar);
      }
    }
    root.querySelector('[data-lot-dupes]')?.addEventListener('change', scheduleDraw);
    wishSearch?.addEventListener('input', searchWish);
    rollBtn?.addEventListener('click', () => {
      if (rolling || root.dataset.lotState === 'pending') {
        pendingRoll = true;
        pendingAdd = false;
        return;
      }
      rolling = true;
      reroll = true;
      drawGen += 1;
      window.clearTimeout(timer);
      if (idleHandle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
      idleHandle = 0;
      // Immediate feedback before await syncCartBlocklist / ensureReservableLot.
      setRollingUi(true);
      draw();
    });
    addBtn?.addEventListener('click', () => {
      if (rolling || root.dataset.lotState === 'pending' || adding) {
        pendingAdd = true;
        return;
      }
      addLot();
    });
  };

  const boot = async () => {
    bind();
    try {
      const [loaded] = await Promise.all([
        poolApi.load({
          handle: collectionHandle,
          setsIndexUrl: root.dataset.setsIndex,
        }),
        syncCartBlocklist(),
      ]);
      catalog = loaded.cards || [];
      sets = loaded.sets || [];
      ready = true;
      renderEnergy();
      renderSets();
      renderPoolCount();
      if (statusEl) {
        const failed = !catalog.length;
        statusEl.textContent = failed ? 'Keine Einzelkarten gefunden.' : `${catalog.length} Einzelkarten geladen.`;
        statusEl.classList.toggle('kl24-builder__notice', failed);
        statusEl.classList.toggle('is-error', failed);
      }
      draw();
    } catch (error) {
      console.warn('[Wunsch-Los]', error);
      if (statusEl) {
        statusEl.textContent = 'Die Karten konnten nicht geladen werden. Bitte lade die Seite neu.';
        statusEl.classList.add('kl24-builder__notice', 'is-error');
      }
    }
  };

  boot();
})();
